import {
  Field,
  Item,
  Image,
  Text,
  ImageField,
  getFieldValue,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames/bind';
import React, { useState } from 'react';
import withValidDatasourceCheck from 'lib/enhancers/withValidDatasourceCheck';

import type { ComponentProps } from 'lib/component-props';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import type { RecordOfFields } from 'models/fields';

import styles from './PodcastListing.module.scss';
import Button from 'components/helpers/Button/Button';
import RichTextWrapper from 'components/helpers/RichText/RichTextWrapper';

type BlogItem = {
  blogTitle: string;
  link: string;
  pubDate: string;
  summary: string;
};

type PodcastListingFields = {
  title: Field<string>;
  titleHeadingLevel: Item | null;
  titleTextStyle: Item | null;
  description: Field<string>;
  blogItems: BlogItem[];
  numberToShowInitially: Field<number>;
  viewMoreItemsCount: Field<number>;
  rssListingItemFallbackImage: ImageField;
  rssListingItemHeadingLevel: Item | null;
};

type PodcastListingProps = ComponentProps & {
  fields: PodcastListingFields;
  id: string;
};

const cx = classNames.bind(styles);

const PodcastListing = (props: PodcastListingProps): React.JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const { className, fields, rendering } = props;
  const { uid } = rendering;

  const titleHeadingLevelItemField = fields.titleHeadingLevel?.fields as RecordOfFields;
  const titleHeadingLevelValue = getFieldValue<string>(titleHeadingLevelItemField, 'Value') || 'h2';

  const titleTextStyleItemField = fields.titleTextStyle?.fields as RecordOfFields;
  const titleTextStyleValue = getFieldValue<string>(titleTextStyleItemField, 'Value') || 'font-style-xl';

  const numberToShowInitially = props.fields.numberToShowInitially?.value || 3;
  const viewMoreCount = props.fields.viewMoreItemsCount?.value || 3;

  const buttonMoreId = `viewMore-button-${uid}`;
  const contentId = `viewMore-content-${uid}`;

  const [viewMore, setViewMore] = useState(false);
  const [displayCount, setDisplayCount] = useState<number>(numberToShowInitially);

  const blogItems = props.fields.blogItems;

  const rssListingItemHeadingLevelField = fields.rssListingItemHeadingLevel?.fields as RecordOfFields;
  const rssListingItemHeadingLevelValue = getFieldValue<string>(rssListingItemHeadingLevelField, 'Value') || 'h3';

  const handleViewMore = () => {
    setViewMore(true);
    const nextDisplayCount = displayCount + viewMoreCount;
    if (nextDisplayCount < blogItems.length) {
      setDisplayCount(nextDisplayCount);
    } else {
      setDisplayCount(blogItems.length);
    }
  };

  return (
    <div className={cx('podcast-listing', className)}>
      <Text
        field={fields.title}
        tag={titleHeadingLevelValue}
        className={cx({ title: true, [titleTextStyleValue]: true })}
        encode={sitecoreContext.pageEditing}
        editable={true}
      />
      <RichTextWrapper field={fields?.description} />
      {blogItems && blogItems?.length > 0 && (
        <div>
          {blogItems?.slice(0, displayCount).map((item: BlogItem, index: number) => (
            <div key={index} className={cx('podcast-listing-card')}>
              <div className={cx('podcast-listing-image-column')}>
                <Image
                  field={props.fields.rssListingItemFallbackImage}
                  className={cx('podcast-listing-image-column__image')}
                />
              </div>
              <div className={cx('blog-column')}>
                <span>{item.pubDate}</span>
                <a target="_blank" href={item.link}>
                  <Text
                    tag={rssListingItemHeadingLevelValue}
                    field={{ value: item.blogTitle.substring('Health Break:'.length).trim() }}
                    className={cx('podcast-listing-card__link')}
                  />
                </a>
                <div>{item.summary?.replace(/&#160;/g, '\u00A0')}</div>
              </div>
            </div>
          ))}
        </div>
      )}
      {blogItems.length > displayCount && (
        <Button
          aria-controls={contentId}
          aria-expanded={viewMore}
          className={styles['viewButton']}
          id={buttonMoreId}
          onClick={handleViewMore}
          type="button"
        >
          {'View More'}
        </Button>
      )}
    </div>
  );
};

export default compose<PodcastListingProps>(withValidDatasourceCheck(), withStyles())(PodcastListing);
